/* eslint-disable */
import React, { useState, useEffect } from 'react';
import axiosInstance from '../apis/axiosConfig';
import { toast } from 'react-toastify';
import { FaBuilding, FaSearch, FaCalendarCheck, FaClock, FaEye, FaTimes, FaCheckCircle, FaTimesCircle, FaTrash } from 'react-icons/fa';
import '../pages_premium.css';
import './PropertyRequests.css';
import { getFullImageUrl } from '../utils/url';
import AdminPagination from '../components/common/AdminPagination';

export default function InspectionsList() {
    const [inspections, setInspections] = useState([]);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [selectedInspection, setSelectedInspection] = useState(null);
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        setCurrentPage(1);
    }, [search, statusFilter]);

    const fetchInspections = async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/admin/inspections');
            setInspections(res.data || []);
        } catch (error) {
            console.error('Error fetching inspections:', error);
            toast.error('Failed to load inspections');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteInspection = async (id) => {
        if (!window.confirm('Are you sure you want to delete this inspection booking?')) {
            return;
        }

        try {
            await axiosInstance.delete(`/admin/inspections/${id}`);
            toast.success('Inspection deleted successfully');
            if (selectedInspection && selectedInspection.id === id) {
                setSelectedInspection(null);
            }
            fetchInspections();
        } catch (error) {
            console.error('Error deleting inspection:', error);
            toast.error(error.response?.data?.message || 'Failed to delete inspection');
        }
    };

    useEffect(() => {
        fetchInspections();
    }, []);

    const handleUpdateStatus = async (inspectionId, newStatus) => {
        try {
            setUpdatingStatus(true);
            await axiosInstance.put(`/admin/inspections/${inspectionId}/status`, { status: newStatus });
            toast.success(`Inspection status updated to ${newStatus}`);
            setInspections(prev => prev.map(insp => insp.id === inspectionId ? { ...insp, status: newStatus } : insp));
            if (selectedInspection && selectedInspection.id === inspectionId) {
                setSelectedInspection(prev => ({ ...prev, status: newStatus }));
            }
        } catch (error) {
            console.error('Error updating inspection status:', error);
            toast.error('Failed to update status');
        } finally {
            setUpdatingStatus(false);
        }
    };

    const formatDate = (val) => {
        if (!val) return '-';
        const dateObj = new Date(val);
        if (!isNaN(dateObj.getTime()) && String(val).includes('-')) {
            const day = String(dateObj.getDate()).padStart(2, '0');
            const month = String(dateObj.getMonth() + 1).padStart(2, '0');
            const year = dateObj.getFullYear();
            return `${day}/${month}/${year}`;
        }
        return String(val);
    };

    const getRoleBadgeClass = (role) => {
        const r = String(role || '').toLowerCase();
        if (r === 'student') return 'role-badge role-badge-student';
        if (r === 'lister' || r === 'agent') return 'role-badge role-badge-lister';
        if (r === 'business owner' || r === 'owner') return 'role-badge role-badge-owner';
        return 'role-badge role-badge-default';
    };

    const getStatusBadge = (status) => {
        const s = String(status || 'booked').toLowerCase();
        if (s === 'completed') {
            return (
                <span className="status-badge status-badge-completed">
                    <FaCheckCircle size={11} /> Completed
                </span>
            );
        }
        if (s === 'cancelled') {
            return (
                <span className="status-badge status-badge-cancelled">
                    <FaTimesCircle size={11} /> Cancelled
                </span>
            );
        }
        return (
            <span className="status-badge status-badge-booked">
                <FaCalendarCheck size={11} /> Booked
            </span>
        );
    };

    const filtered = inspections.filter(insp => {
        const matchesSearch = 
            insp.property_title?.toLowerCase().includes(search.toLowerCase()) ||
            insp.user_name?.toLowerCase().includes(search.toLowerCase()) ||
            insp.user_email?.toLowerCase().includes(search.toLowerCase()) ||
            insp.listed_by?.toLowerCase().includes(search.toLowerCase()) ||
            insp.inspection_date?.toLowerCase().includes(search.toLowerCase());

        const matchesFilter = statusFilter === 'all' || String(insp.status).toLowerCase() === statusFilter.toLowerCase();
        return matchesSearch && matchesFilter;
    });

    const paginatedInspections = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    return (
        <div className="page-premium-wrapper properties-list-page">
            <div className="page-premium-content">

                {/* Hero Header */}
                <div className="page-hero-header page-hero-properties">
                    <div className="ph-blob ph-blob-1"></div>
                    <div className="ph-blob ph-blob-2"></div>

                    <div className="ph-left">
                        <div className="ph-icon" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
                            <FaCalendarCheck size={26} />
                        </div>
                        <div>
                            <h1 className="ph-title">Property Inspections</h1>
                            <p className="ph-subtitle">Monitor and manage all confirmed and booked room inspection slots</p>
                        </div>
                    </div>

                    <div className="ph-right">
                        <div className="ph-count-badge">
                            <span className="count-num">{inspections.length}</span>
                            <span className="count-lbl">Total Inspections</span>
                        </div>
                    </div>
                </div>

                {/* Table Card */}
                <div className="premium-table-card">
                    <div className="ptable-toolbar" style={{ flexWrap: 'wrap', gap: '12px' }}>
                        <div className="ptable-title">
                            <span className="ptable-title-dot" style={{ background: '#059669' }}></span>
                            Confirmed Inspections List
                        </div>

                        {/* Status Filter Buttons */}
                        <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                            {['all', 'booked', 'completed', 'cancelled'].map(st => (
                                <button
                                    key={st}
                                    type="button"
                                    onClick={() => setStatusFilter(st)}
                                    style={{
                                        padding: '5px 12px',
                                        fontSize: '0.78rem',
                                        fontWeight: 600,
                                        borderRadius: '20px',
                                        border: statusFilter === st ? '1px solid #059669' : '1px solid #e2e8f0',
                                        background: statusFilter === st ? '#ecfdf5' : '#ffffff',
                                        color: statusFilter === st ? '#047857' : '#64748b',
                                        cursor: 'pointer',
                                        textTransform: 'capitalize',
                                        transition: 'all 0.15s'
                                    }}
                                >
                                    {st}
                                </button>
                            ))}
                        </div>

                        <div className="ptable-search">
                            <FaSearch className="ptable-search-icon" />
                            <input
                                type="text"
                                placeholder="Search by property, tenant, date..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="ptable-wrap">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '50px' }}>#</th>
                                    <th style={{ minWidth: '220px' }}>Property</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>User / Interested User</th>
                                    <th style={{ whiteSpace: 'nowrap' }}>Listed By</th>
                                    <th>Listed Type</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Inspection Date</th>
                                    <th>Inspection Time</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="11" style={{ textAlign: 'center', padding: '40px 0' }}>
                                            <div className="spinner-border text-success" role="status">
                                                <span className="sr-only">Loading...</span>
                                            </div>
                                            <p className="mt-2 text-muted" style={{ fontSize: '0.85rem' }}>Loading inspection records...</p>
                                        </td>
                                    </tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan="11">
                                            <div className="ptable-empty">
                                                <div className="ptable-empty-icon">📅</div>
                                                <p>No inspections booked.</p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    paginatedInspections.map((insp, idx) => (
                                        <tr key={insp.id}>
                                            <td style={{ color: '#9ba6c0', fontWeight: 600 }}>#{(currentPage - 1) * pageSize + idx + 1}</td>
                                            <td style={{ minWidth: '220px' }}>
                                                <div className="prop-title-cell">
                                                    {insp.property_thumbnail ? (
                                                        <img
                                                            src={getFullImageUrl(insp.property_thumbnail)}
                                                            alt="Property Thumbnail"
                                                            className="prop-thumb"
                                                        />
                                                    ) : (
                                                        <div className="prop-thumb-fallback">
                                                            <FaBuilding size={14} />
                                                        </div>
                                                    )}
                                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                                        <strong className="prop-title-text">{insp.property_title}</strong>
                                                        <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>{insp.property_address}</span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}>
                                                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#dcfce7', color: '#15803d', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '11px' }}>
                                                        {insp.user_name ? insp.user_name.charAt(0).toUpperCase() : 'U'}
                                                    </div>
                                                    <span style={{ fontWeight: 600, color: '#0f172a', whiteSpace: 'nowrap' }}>{insp.user_name}</span>
                                                </div>
                                            </td>
                                            <td style={{ whiteSpace: 'nowrap' }}>
                                                <span style={{ fontWeight: 500, color: '#334155', whiteSpace: 'nowrap' }}>{insp.listed_by}</span>
                                            </td>
                                            <td>
                                                <span className={getRoleBadgeClass(insp.listed_type)}>
                                                    {insp.listed_type}
                                                </span>
                                            </td>
                                            <td>{insp.user_email || '-'}</td>
                                            <td>{insp.user_phone || '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600, color: '#1e293b' }}>
                                                    <FaCalendarCheck size={12} className="text-success" />
                                                    {formatDate(insp.inspection_date)}
                                                </div>
                                            </td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#475569', fontSize: '0.82rem' }}>
                                                    <FaClock size={11} className="text-muted" />
                                                    {insp.inspection_time || '10:00 AM'}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                {getStatusBadge(insp.status)}
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', justifyContent: 'center' }}>
                                                    <button
                                                        type="button"
                                                        className="action-btn action-btn-view"
                                                        data-tooltip="View Inspection Details"
                                                        onClick={() => setSelectedInspection(insp)}
                                                    >
                                                        <FaEye />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="action-btn action-btn-delete"
                                                        data-tooltip="Delete Inspection"
                                                        onClick={() => handleDeleteInspection(insp.id)}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <AdminPagination
                        currentPage={currentPage}
                        totalItems={filtered.length}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                        itemLabel="inspections"
                    />
                </div>

            </div>

            {/* Inspection Details Modal */}
            {selectedInspection && (
                <div className="modal-overlay" onClick={() => setSelectedInspection(null)}>
                    <div className="modal-dialog-custom" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header-custom">
                            <h3>Inspection Booking Details</h3>
                            <button
                                type="button"
                                className="modal-close-btn"
                                onClick={() => setSelectedInspection(null)}
                            >
                                <FaTimes size={18} />
                            </button>
                        </div>
                        <div className="modal-body-custom">
                            <div className="modal-field">
                                <div className="modal-label">Property</div>
                                <div className="modal-val" style={{ fontSize: '1rem', fontWeight: 700 }}>
                                    {selectedInspection.property_title}
                                </div>
                                <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                    {selectedInspection.property_address}
                                </div>
                            </div>

                            <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                                <div className="admin-2col-grid" style={{ gap: '14px' }}>
                                    <div>
                                        <div className="modal-label">Confirmed Date</div>
                                        <div className="modal-val" style={{ color: '#047857', fontWeight: 700 }}>
                                            📅 {formatDate(selectedInspection.inspection_date)}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="modal-label">Confirmed Time</div>
                                        <div className="modal-val" style={{ fontWeight: 700 }}>
                                            ⏰ {selectedInspection.inspection_time || '10:00 AM'}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="admin-2col-grid" style={{ gap: '14px', marginBottom: '14px' }}>
                                <div>
                                    <div className="modal-label">Attending User</div>
                                    <div className="modal-val">{selectedInspection.user_name}</div>
                                </div>
                                <div>
                                    <div className="modal-label">User Email</div>
                                    <div className="modal-val">{selectedInspection.user_email || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">User Phone</div>
                                    <div className="modal-val">{selectedInspection.user_phone || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Property Lister</div>
                                    <div className="modal-val">{selectedInspection.listed_by}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Lister Role</div>
                                    <div className="modal-val">
                                        <span className={getRoleBadgeClass(selectedInspection.listed_type)}>
                                            {selectedInspection.listed_type}
                                        </span>
                                    </div>
                                </div>
                                <div>
                                    <div className="modal-label">Lister Email</div>
                                    <div className="modal-val">{selectedInspection.owner_email || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Request / Conv ID</div>
                                    <div className="modal-val">#{selectedInspection.request_id || '-'}</div>
                                </div>
                                <div>
                                    <div className="modal-label">Current Status</div>
                                    <div className="mt-1">{getStatusBadge(selectedInspection.status)}</div>
                                </div>
                            </div>

                            {/* Status Change Control */}
                            <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
                                <div className="modal-label" style={{ marginBottom: '8px' }}>Update Status:</div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                        type="button"
                                        disabled={updatingStatus || selectedInspection.status === 'booked'}
                                        onClick={() => handleUpdateStatus(selectedInspection.id, 'booked')}
                                        className="btn btn-sm btn-outline-primary"
                                    >
                                        Mark as Booked
                                    </button>
                                    <button
                                        type="button"
                                        disabled={updatingStatus || selectedInspection.status === 'completed'}
                                        onClick={() => handleUpdateStatus(selectedInspection.id, 'completed')}
                                        className="btn btn-sm btn-outline-success"
                                    >
                                        Mark as Completed
                                    </button>
                                    <button
                                        type="button"
                                        disabled={updatingStatus || selectedInspection.status === 'cancelled'}
                                        onClick={() => handleUpdateStatus(selectedInspection.id, 'cancelled')}
                                        className="btn btn-sm btn-outline-danger"
                                    >
                                        Mark as Cancelled
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer-custom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}
                                onClick={() => handleDeleteInspection(selectedInspection.id)}
                            >
                                <FaTrash size={11} /> Delete Inspection
                            </button>
                            <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={() => setSelectedInspection(null)}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
